import { useMemo } from 'react'
import type { AgentContactRecord } from '@/lib/agent-contact-utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { UnifiedContactForm } from '@/components/ui/unified-contact-form'
import { toDisplayString } from '../utils/display'
import type { ContactFormController } from './useContactForm'

interface ContactSectionProps {
  contactMatch?: AgentContactRecord
  contactDisplayName?: string
  contactIdDisplay: string | null
  isSupervisor: boolean
  form: ContactFormController
}

interface ContactDetailEntry {
  label: string
  value: string
  link?: string
}

const buildContactDetailEntries = (
  contact?: AgentContactRecord
): ContactDetailEntry[] => {
  if (!contact) {
    return []
  }

  const entries: ContactDetailEntry[] = []

  const email = toDisplayString(contact.email)
  if (email) {
    entries.push({
      label: 'Email',
      value: email,
      link: `mailto:${email}`,
    })
  }

  const phone = toDisplayString(contact.phoneNumber)
  if (phone) {
    entries.push({
      label: 'Phone',
      value: phone,
      link: `tel:${phone.replace(/\s+/g, '')}`,
    })
  }

  const title = toDisplayString(contact.title)
  if (title) {
    entries.push({
      label: 'Title',
      value: title,
    })
  }

  const messenger = toDisplayString(contact.fbMessengerId)
  if (messenger) {
    entries.push({
      label: 'Facebook Messenger',
      value: messenger,
    })
  }

  const telegram = toDisplayString(contact.telegramUserId || contact.telegramId)
  if (telegram) {
    entries.push({
      label: 'Telegram',
      value: telegram,
    })
  }

  const instagram = toDisplayString(contact.instagramId)
  if (instagram) {
    entries.push({
      label: 'Instagram',
      value: instagram,
    })
  }

  const weChat = toDisplayString(contact.weChatId)
  if (weChat) {
    entries.push({
      label: 'WeChat',
      value: weChat,
    })
  }

  const line = toDisplayString(contact.lineId)
  if (line) {
    entries.push({
      label: 'LINE',
      value: line,
    })
  }

  const whatsappId = toDisplayString(contact.waId)
  if (whatsappId) {
    entries.push({
      label: 'WhatsApp ID',
      value: whatsappId,
    })
  }

  return entries
}

export function ContactSection({
  contactMatch,
  contactDisplayName,
  contactIdDisplay,
  isSupervisor,
  form,
}: ContactSectionProps) {
  const {
    isContactDialogOpen,
    contactFormMode,
    contactFormValues,
    contactFormErrors,
    isContactSubmitting,
    contactMutationErrorMessage,
    handleContactDialogOpenChange,
    openCreateContactDialog,
    openEditContactDialog,
    handleContactFormSubmit,
  } = form

  const contactDetailEntries = useMemo(
    () => buildContactDetailEntries(contactMatch),
    [contactMatch]
  )
  const hasContactDetails = contactDetailEntries.length > 0
  const canEditContact = Boolean(isSupervisor && contactMatch)
  const canCreateContact = !contactMatch

  return (
    <>
      <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Contact
            </p>
            <p className="text-sm font-semibold text-gray-900">
              {contactMatch
                ? contactDisplayName || 'Unnamed contact'
                : 'No saved contact'}
            </p>
            {contactIdDisplay && (
              <p className="text-xs text-gray-500">
                Contact ID: {contactIdDisplay}
              </p>
            )}
          </div>
          {contactMatch && canEditContact ? (
            <Button
              variant="outline"
              size="sm"
              onClick={openEditContactDialog}
              disabled={isContactSubmitting}
            >
              {isContactSubmitting ? 'Saving...' : 'Edit'}
            </Button>
          ) : null}
        </div>
        {contactMatch ? (
          hasContactDetails ? (
            <dl className="mt-3 space-y-2">
              {contactDetailEntries.map(entry => (
                <div key={entry.label} className="flex flex-col">
                  <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {entry.label}
                  </span>
                  {entry.link ? (
                    <a
                      href={entry.link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-medium text-blue-600 hover:text-blue-700 break-words"
                    >
                      {entry.value}
                    </a>
                  ) : (
                    <span className="text-sm font-medium text-gray-900 break-words">
                      {entry.value}
                    </span>
                  )}
                </div>
              ))}
            </dl>
          ) : (
            <p className="mt-3 text-xs text-gray-600">
              No additional contact details saved yet.
            </p>
          )
        ) : (
          <p className="mt-3 text-xs text-gray-600">
            We could not find a saved contact for this session.
          </p>
        )}
        {canCreateContact && (
          <Button
            className="mt-3 w-full"
            onClick={openCreateContactDialog}
            disabled={isContactSubmitting}
          >
            {isContactSubmitting ? 'Creating...' : 'Create contact'}
          </Button>
        )}
        {contactMatch && !canEditContact ? (
          <p className="mt-3 text-xs text-gray-500">
            Only supervisors can update contact details.
          </p>
        ) : null}
      </div>

      <Dialog
        open={isContactDialogOpen}
        onOpenChange={handleContactDialogOpenChange}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {contactFormMode === 'edit' ? 'Update contact' : 'Create contact'}
            </DialogTitle>
            <DialogDescription>
              Store contact details so your team can recognise this visitor in
              future conversations.
            </DialogDescription>
          </DialogHeader>

          <UnifiedContactForm
            initialData={contactFormValues}
            onSubmit={handleContactFormSubmit}
            onCancel={() => handleContactDialogOpenChange(false)}
            isLoading={isContactSubmitting}
            mode={contactFormMode}
            variant="dialog"
            errors={contactFormErrors}
            formError={contactMutationErrorMessage}
            submitButtonText={
              isContactSubmitting
                ? contactFormMode === 'edit'
                  ? 'Saving...'
                  : 'Creating...'
                : contactFormMode === 'edit'
                  ? 'Save changes'
                  : 'Create contact'
            }
            cancelButtonText="Cancel"
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
